import { createNamespacedHelpers } from 'vuex';
import { DefineActions, DefineGetters, DefineMutations } from 'vuex-type-helper';
import { AppData, Getters, Mutations, Actions, Status, AccessLevel } from '@/models/App.model';
import { LoggedInUser } from '@/models/User.model';
import dayjs from 'dayjs';

// Inital state
const state: AppData = {

  snackbar: {
    show: false,
    text: '',
    color: 'error',
    mode: '',
    timeout: Number(process.env.VUE_APP_SNACKBAR_TIMEOUT),
    x: '',
    y: 'top',
    icon: '',
    closeIcon: 'mdi-close-circle-outline',
  },

  statuses: [],
};

// Gets the values of our state usually called from components
const getters: DefineGetters<Getters, AppData> = {
  icons: (state) => {
    return {
      // ----- DO NOT EDIT GENERIC ICONS !! -----
      // --- generic --- ----- DO NOT EDIT GENERIC ICONS !! -----
        // -- informational ----- DO NOT EDIT GENERIC ICONS !! -----
        date: 'mdi-calendar-outline',
        email: 'mdi-email',
        error: 'mdi-alert',
        favourite: 'mdi-star',
        info: 'mdi-information',
        inProgress: 'mdi-progress-clock',
        no: 'mdi-close-thick',
        password: 'mdi-lock',
        pdf: 'mdi-file-pdf-box',
        phone: 'mdi-phone-outline',
        profile: 'mdi-account',
        settings: 'mdi-cog',
        success: 'mdi-check-circle',
        telegram: 'mdi-send',
        time: 'mdi-clock',
        username: 'mdi-account',
        warning: 'mdi-bell',
        web: 'mdi-web',
        whatsapp: 'mdi-whatsapp',
        yes: 'mdi-check-bold',
        // --- actions --- ----- DO NOT EDIT GENERIC ICONS !! -----
        activate: 'mdi-play',
        add: 'mdi-plus-box',
        close: 'mdi-close-circle-outline',
        closeSideMenu: 'mdi-chevron-left',
        closeSideMenuMobile: 'mdi-close-thick',
        contract: 'mdi-menu-up', 
        copy: 'mdi-content-copy', 
        delete: 'mdi-delete',
        download: 'mdi-download',
        edit: 'mdi-pencil',
        expand: 'mdi-menu-down', 
        filter: 'mdi-filter-variant',
        login: 'mdi-fingerprint',
        logout: 'mdi-logout-variant',
        next: 'mdi-chevron-right',
        openSideMenu: 'mdi-chevron-right',
        openSideMenuMobile: 'mdi-menu',
        passwordShow: 'mdi-eye',
        passwordHide: 'mdi-eye-off',
        previous: 'mdi-chevron-left',
        print: 'mdi-printer',
        refresh: 'mdi-refresh-circle',
        register: 'mdi-account-plus',
        search: 'mdi-magnify',
        suspend: 'mdi-pause',
        upload: 'mdi-paperclip',
        view: 'mdi-eye',
      // ----- DO NOT EDIT GENERIC ICONS !! -----
        
      // --- app specific ---
        // ------------- README: -----------
        // Try not to re-use icons unless they really mean / are doing the same thing. 
        // Rather add a new icon type because the icon could be changed 
        // down the line and you might not want it changed in different use-cases
        // ------------- ------- -----------

        // --- informational (alphabetical) ---
        additional: 'mdi-plus',
        cofIndicator: 'mdi-alpha-c-box-outline', // 'mdi-certificate',
        critical: 'mdi-star', 
        decrease: 'mdi-arrow-down-bold',
        driverOnLeave: 'mdi-calendar-star', //'mdi-home-alert',
        hasPdpReceipt: 'mdi-card-account-details-star-outline', //'mdi-note-text-outline',
        //horse: 'mdi-horse-variant',
        increase: 'mdi-arrow-up-bold',
        inYard: 'mdi-home',
        licenseRenewal: 'mdi-alert-decagram',
        linkedTrailer: 'mdi-link-variant',
        linkedTrailerFront: 'mdi-alpha-f-circle-outline',
        linkedTrailerRear: 'mdi-alpha-r-circle-outline',
        loggedInUser: 'mdi-shield-account',
        nextServiceWarning: 'mdi-alpha-s-box', //'mdi-gauge',
        nextWheelWarning: 'mdi-album', // 'mdi-tire',
        notCritical: 'mdi-star-outline',
        pdpExpiryWarning: 'mdi-card-account-details',
        phoneClassic: 'mdi-phone-classic',
        primary: 'mdi-star-circle-outline',
        priorityLoad: 'mdi-alert-decagram',
        //trailer: 'mdi-truck-trailer',
        vehicleSuspended: 'mdi-pause-circle',

          // --- vehicle & load statuses ---
          /*
          preLoad: 'mdi-progress-clock',
          newLoad: 'mdi-new-box',
          loading: 'mdi-dolly',
          enRoute: 'mdi-truck-fast',
          arrived: 'mdi-map-marker-check',
          offHooked: 'mdi-hook-off',

          waitApprove: 'mdi-clipboard-check-outline',
          waitPodScan: 'mdi-scanner',
          scanned: 'mdi-file-sign',
          rejected: 'mdi-alert-circle',

          offLoaded: 'mdi-truck-outline',
          accident: 'mdi-tow-truck',
          disposed: 'mdi-cancel',
          */

        // --- actions (alphabetical) --- 
        addExtra: 'mdi-cash-plus',
        addLineItem: 'mdi-text-box-plus-outline',
        addPriority: 'mdi-plus', //'mdi-check-decagram',
        adjustRates: 'mdi-swap-vertical-bold',
        applyZeroRates: 'mdi-cancel', //'mdi-numeric-0-box',
        approveJobCard: 'mdi-check-bold',
        approveLoad: 'mdi-check-bold',
        changeHorse: 'mdi-horse-variant',
        changeTrailer: 'mdi-truck-trailer',
        closeJobCard: 'mdi-lock',
        comment: 'mdi-comment-text-outline',
        disposeVehicle: 'mdi-cancel',
        editOrderNumber: 'mdi-barcode',
        finalisePayment: 'mdi-cash-lock',
        offHookTrailer: 'mdi-hook-off',
        refreshOverride: 'mdi-sync',
        rejectLoad: 'mdi-close-thick',
        restore: 'mdi-delete-restore',
        reviewDriverLeave: 'mdi-alert-decagram',

        searchCustomers: 'mdi-briefcase-account',
        searchDate: 'mdi-calendar-range',
        searchDriver: 'mdi-seatbelt',
        searchInvoiceNumber: 'mdi-numeric',
        searchLoadNumber: 'mdi-counter',
        searchStatus: 'mdi-map-marker-question',
        searchTruck: 'mdi-horse-variant',

        sms: 'mdi-bullhorn-variant', //'mdi-cellphone-message',
        updateLoadStatus: 'mdi-arrow-right-bold-hexagon-outline',

        // --- nav (always match order on front end) ---
        users: 'mdi-account-multiple',
        customers: 'mdi-briefcase-account',
        vehicles: 'mdi-truck',
        drivers: 'mdi-seatbelt',
          driverTypes: 'mdi-account-cash',
          driverRoutes: 'mdi-routes',
          driverRollouts: 'mdi-format-list-checks',
          driverPayments: 'mdi-cash-register',
          leave: 'mdi-calendar-multiple-check', //'mdi-calendar-multiselect',
        rates: 'mdi-cash-multiple',
          routeRates: 'mdi-routes',
          rateAdjustments: 'mdi-swap-vertical-bold',
        bulkMessage: 'mdi-bullhorn-variant', //'mdi-cellphone-message',
        movement: 'mdi-map-marker-path',
          availableHorses: 'mdi-truck-outline',
        approvals: 'mdi-shield-check',
        workshop: 'mdi-hammer-wrench',
          adhocMaintenance: 'mdi-wrench-clock',
          dailyYardList: 'mdi-calendar-today',
          jobCards: 'mdi-id-card',
          workshopSummary: 'mdi-chart-bar',
        loads: 'mdi-shipping-pallet',
        reportSchedule: 'mdi-clipboard-play',

        // --- profile nav (always match order on front end) ---
        // --- additional (alphabetical) ---
        addCircle: 'mdi-plus-circle',
    };
  },

  messages: (state) => {
    return {
      couldNotConnect: 'Error: Please contact Interland Distribution for assistance.',
      sessionTimeout: 'Your session has timed out, please login to continue.',
    };
  },

  snackbar: (state) => state.snackbar,

  statusHelper: (state) => {
    return {
      preload: 1,
      new: 2,
      loading: 3,
      enRoute: 4,
      arrived: 5,
      offhooked: 6,
      waitOrderNumber: 9,
      waitOpprove: 10,
      rejected: 11,
      waitPodScan: 15,
      scanned: 20,
      waitInvoiceNo: 25,
      waitProcessing: 30,
      waitInvoiceSend: 35,
      loadInvoiced: 40,
      emptyLoadCompleted: 41,
      deleted: 44,
      offloaded: 45,
      accident: 46,
      disposed: 47,
    };
  },

  avatarSize: (state) => 30,

  statuses: (state) => state.statuses,

  approvalStatuses: (state) => {
    // 9: WAIT_ORDER_NUMBER
    // 10: WAIT_OPPROVE
    // 11: REJECTED
    // 15: WAIT_POD_SCAN
    // 20 :SCANNED
    // 25: WAIT_INVOICE_NO
    // 30: WAIT_PROCESSING
    // 35: WAIT_INVOICE_SEND
    const approvalStatusIds = [9, 10, 11, 15, 20, 25, 30, 35];

    // I expect that at some point this list will be controlled by the User's Clearance Level

    return state.statuses.filter((item: Status) => approvalStatusIds.includes(item.id));
  },

  // https://codeburst.io/vuex-getters-are-great-but-dont-overuse-them-9c946689b414
  // from what I gather, in order to make a getter update itself, it needs to be parameterised
  // this was not working previously using getters on the actual vue files, even when they were
  // calling a method in AppService. They would not refresh on each call.
  // because we need this to be a generic global method, I have written it here as a getter.
  autoId: (state) => (random: string): string => {
    let autoId: string = ''; 
    const dict: string = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'; 
    
    for (let i = 0; i < 12; i++) { 
      autoId += dict.charAt(Math.floor(Math.random() * dict.length)); 
    } 

    return autoId;
  },

  maxWidthButton: (state) => (currentBreakpoint: string, maxWidthBreakpoints: string[]): string => {
    let returnValue = '';
    for (const breakpoint of maxWidthBreakpoints) {
      if (currentBreakpoint === breakpoint) {
        returnValue = '100%';
        break;
      }
    }

    return returnValue;
  },

  allowAccess: (state) => (loggedInUser: LoggedInUser, allowedLevels: AccessLevel[]): boolean => {
    let returnVal = false;
    for (var level of allowedLevels) {
      if ((loggedInUser.type === level.type) && (loggedInUser.clearanceLevel >= level.level)) {
        returnVal = true;
        break;
      }
    }

    return returnVal;
  },

  displayAsCurrency: (state) => (value: number | string): string  => {
    return 'R ' + parseFloat('' + value).toFixed(2).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  },

  getInitials: (state) => (name: string): string => {
    if (name) {
      name = (name.indexOf('(') > 0) ? name.substring(0, name.indexOf('(')) : name;
      const initials = name.replace(/[^a-zA-Z- ]/g, '').match(/\b\w/g);
      return (initials != null) ? initials.join('') : '';
    } else {
      return '';
    }
  },

  statusById: (state) =>  (statusId: number): Status | undefined => {
    return state.statuses.find((item: Status) => item.id === statusId);
  }, 
};

// TODO: Change mutation names to uppercase ( Standards )
// Sets values or changes to state. Must be called via an action.
const mutations: DefineMutations<Mutations, AppData> = {
  snackbar(state, { show, text, color, icon, timeout }) {
    state.snackbar.show = show;
    state.snackbar.text = text;
    state.snackbar.color = color;
    state.snackbar.icon = icon;
    state.snackbar.timeout = timeout;
  },

  setStatuses(state, { statuses }) {
    state.statuses = statuses;
  },
};

// Async functions that will contain the logic / service calls and commit mutations to state
const actions: DefineActions<Actions, AppData, Mutations, Getters> = {
  snackbar({ commit }, payload) {
    commit('snackbar', payload);
  },

  setStatuses({ commit }, payload) {
    commit('setStatuses', payload);
  },
};

export const {
  mapState,
  mapGetters,
  mapMutations,
  mapActions,
} = createNamespacedHelpers<AppData, Getters, Mutations, Actions>(
  'AppModule',
);

export const AppModule = {
  namespaced: true,
  state: state,
  getters: getters,
  mutations: mutations,
  actions: actions,
};
